<script setup>
import ConsentBanner from './views/ConsentBanner.vue';
import dataService from '@/services/dataService';
import { onMounted, provide } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

// Créer un bus d'événements simplifié pour la communication entre composants
const eventBus = {
  deliveryUpdated: (deliveryId, updates = {}) => {
    // Émettre un événement personnalisé quand une livraison est mise à jour
    const event = new CustomEvent('delivery-updated', { 
      detail: { 
        deliveryId,
        updates, // Inclure les mises à jour spécifiques
        timestamp: Date.now()
      } 
    });
    window.dispatchEvent(event);
    console.log(`Événement delivery-updated émis pour la livraison ${deliveryId}`);
  }
};

// Fournir le bus d'événements à tous les composants enfants
provide('eventBus', eventBus);

// Fonction pour récupérer les données en cache au démarrage, spécialement utile hors ligne
const attemptOfflineDataRecovery = async () => {
  try {
    console.log("Tentative de récupération des données en mode hors ligne...");
    
    // Utiliser la nouvelle fonction dédiée pour s'assurer que les données sont disponibles
    const dataAvailable = await dataService.ensureOfflineDataAvailability();
    
    if (!dataAvailable) {
      console.log("Aucune tournée trouvée en cache");
      return false;
    }
    
    // Récupérer la tournée depuis le cache local via dataService
    const cachedTournee = await dataService.getTournee('current');
    
    if (cachedTournee) {
      console.log("Tournée récupérée depuis le cache local :", cachedTournee.id);
      
      // Mettre à jour le store avec les données en cache
      store.commit('SET_TOURNEE_DATA', cachedTournee);
      store.commit('SET_LAST_TOURNEE_UPDATE', Date.now());
      
      // Si l'utilisateur était sur une page de détail de livraison, récupérer cette livraison
      const currentRoute = router.currentRoute.value;
      if (currentRoute.name === 'delivery-detail' && currentRoute.params.id) {
        const deliveryId = currentRoute.params.id;
        console.log("Tentative de récupération de la livraison", deliveryId);
        
        // Chercher d'abord dans la tournée en cache
        const deliveryFromTournee = cachedTournee.deliveries.find(d => d.id == deliveryId);
        
        if (deliveryFromTournee) {
          store.commit('SET_CURRENT_DELIVERY', deliveryFromTournee);
          console.log("Livraison récupérée depuis la tournée en cache");
        } else {
          // Sinon, essayer de récupérer directement depuis le cache des livraisons
          const cachedDelivery = await dataService.getDelivery(deliveryId);
          if (cachedDelivery) {
            store.commit('SET_CURRENT_DELIVERY', cachedDelivery);
            console.log("Livraison récupérée depuis le cache individuel");
          }
        }
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Erreur lors de la récupération des données hors ligne:", error);
    return false;
  }
};

onMounted(async () => {
  console.log("Application chargée, statut en ligne :", navigator.onLine);
  try {
    // Initialiser le service de données
    await dataService.init();
    console.log("Service de données initialisé avec succès");
    
    // Si l'application démarre en mode hors ligne, essayer de récupérer les données du cache
    if (!navigator.onLine || store.getters.isUserOfflineMode) {
      console.log("Démarrage en mode hors ligne détecté");
      const recovered = await attemptOfflineDataRecovery();
      if (recovered) {
        console.log("Données récupérées avec succès pour le mode hors ligne");
      }
    }

    // Ajouter des écouteurs d'événements pour les transitions online/offline spécifiques aux rechargements de page
    window.addEventListener('beforeunload', () => {
      // Stocker l'état de connexion actuel avant le rechargement
      localStorage.setItem('lastConnectionState', navigator.onLine ? 'online' : 'offline');
      
      // Si en hors ligne, s'assurer que la dernière tournée est sauvegardée
      if (!navigator.onLine && store.state.tourneeData) {
        // Force une sauvegarde de la tournée courante
        dataService.saveTournee('current', store.state.tourneeData);
      }
    });

    // Vérifier si on vient d'un rechargement en mode hors ligne
    const lastConnectionState = localStorage.getItem('lastConnectionState');
    if (lastConnectionState === 'offline' && !navigator.onLine) {
      console.log("Retour d'un rechargement en mode hors ligne, données restaurées");
    }
  } catch (error) {
    console.error("Erreur lors de l'initialisation du service de données:", error);
    store.dispatch('logs/error', {
      message: "Erreur lors de l'initialisation du service de données",
      data: { error: error.message }
    });
  }

  // Intercepter les mutations du store pour détecter les mises à jour de livraison
  store.subscribe((mutation, state) => {
    if (mutation.type === 'UPDATE_DELIVERY_IN_TOURNEE') {
      const { deliveryId } = mutation.payload;
      eventBus.deliveryUpdated(deliveryId);
    }
  });
});
</script>

<template>
  <router-view />
  <ConsentBanner />
</template>

<style scoped></style>
